import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid, GridCellEditStopReasons,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../../components/spinner';
import { apiService } from '../../api/axiosClient';
import StyledDataGrid from '../../styles/StyledDataGrid';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import { fDate } from '../../utils/formatTime';
import OrderToolbar from './OrderToolbar';

const currencyLocale = Intl.NumberFormat('ko-KR')

const TableOrderManage = (props) => {
  const { filters, submit, selected, setSelected, page, setPage, pageSize, setPageSize } = props
  const [reload, setReload] = useState(0)
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('desc')
  const [list, setList] = useState([])
  const [sortColumn, setSortColumn] = useState('order_time')

  const loadColumnOrderTable = window.localStorage.getItem('TableOrderManage') ? 
    JSON.parse(window.localStorage.getItem('TableOrderManage')) : 
    {
      remarks: true,
      order_status: true,
    };
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(loadColumnOrderTable);

  const loadOrders = 
    async (sort, column) => {
      try {
        const response = await apiService.get('/proc/ordermanage', {
          params: {
            ...filters,
            sort,
            column,
            page,
            pageSize
          }
        });
        const payload = response.data;

        setTotal(payload.size);
        setList(payload.list);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    }

  useEffect(() => {
    const {startDate, endDate} = filters;

    if(!isEmpty(startDate) && !isEmpty(endDate)) {
      loadOrders(sort, sortColumn);
    }
  }, [page, pageSize, reload, submit, filters])

  useEffect(() => {
    window.localStorage.setItem( 'TableOrderManage', JSON.stringify(columnVisibilityModel) );
    setColumnVisibilityModel(columnVisibilityModel);
  }, [columnVisibilityModel])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadOrders(newModel[0].sort, newModel[0].field)
    } else {
      setSort('asc')
      setSortColumn('order_time')
    }
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }  

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const handleCheckboxChange = async (cartid, field, checked) => {
    const updatedValue = checked ? '1' : '0';
  
    setList((prevList) =>
      prevList.map((item) =>
        item.cartid === cartid ? { ...item, [field]: updatedValue } : item
      )
    );
  }

  const handleStateChange = async (cartid, field, newValue) => {
    const updatedValue = newValue;

    setList((prevList) =>
      prevList.map((item) =>
        item.cartid === cartid ? { ...item, [field]: updatedValue } : item
      )
    );
  }

  const columns = [
    {
      field: '__check__',
      hideable: false,
      sortable: false,
      filterable: false,
      headerAlign: 'left'
    },
    {
      field: 'order_time',
      hideable: false,
      headerName: '주문일자',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {fDate(params.row.order_time)}
        </Typography>
      )
    },
    {
      field: 'orderid',
      hideable: false,
      headerName: '주문번호',
      minWidth: 200,
      renderCell: params => (
        <Link to={`/admin/order/${params.row.orderid}`}>
          {params.row.orderid}
        </Link>
      )
    },
    {
      field: 'user_id',
      hideable: false,
      headerName: '회원 ID',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.user_id}
        </Typography>
      )
    },
    {
      field: 'sales_manager',
      hideable: false,
      headerName: '영업담당',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.sales_manager}
        </Typography>
      )
    },
    {
      field: 'business_type',
      hideable: false,
      headerName: '업체분류',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.business_type}
        </Typography>
      )
    },
    {
      field: 'company_name',
      hideable: false,
      headerName: '회사명',
      renderCell: params => (
        <Link to={`/admin/company/${params.row.com_id}`} >
          {params.row.company_name}
        </Link>
      )
    },
    {
      field: 'orderer',
      hideable: false,
      headerName: '주문자',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.orderer}
        </Typography>
      )
    },
    {
      field: 'catalog_no',
      hideable: false,
      headerName: 'Cat No.',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.catalog_no}
        </Typography>
      )
    },
    {
      field: 'product_name',
      hideable: false,
      headerName: '제품명',
      minWidth: 200,
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.product_name}
        </Typography>
      )
    },
    {
      field: 'cas_number',
      hideable: false,
      headerName: 'CAS',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.cas_number}
        </Typography>
      )
    },
    {
      minWidth: 300,
      field: 'specification',
      hideable: false,
      headerName: '사양',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.specification}
        </Typography>
      )
    },
    {
      field: 'packing',
      hideable: false,
      headerName: '패킹',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.packing}
        </Typography>
      )
    },
    {
      field: 'quantity',
      hideable: false,
      headerName: '수량',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.quantity}
        </Typography>
      )
    },
    {
      field: 'purchase_amount',
      hideable: false,
      headerName: '원가',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.purchase_amount)}
        </Typography>
      )
    },
    {
      field: 'unit_price',
      hideable: false,
      headerName: '단가',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.unit_price)}
        </Typography>
      )
    },
    {
      field: 'supply_price',
      hideable: false,
      headerName: '공급가액',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.supply_price)}
        </Typography>
      )
    },
    {
      field: 'order_amount',
      hideable: false,
      headerName: '주문가',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.order_amount)}
        </Typography>
      )
    },
    {
      field: 'storage',
      hideable: false,
      headerName: '보관온도',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.storage}
        </Typography>
      )
    },
    {
      field: 'pd_buy_price',
      hideable: false,
      headerName: '원가',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.pd_buy_price}
        </Typography>
      )
    },
    {
      field: 'payment_terms',
      hideable: false,
      headerName: '결제조건',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.payment_terms}
        </Typography>
      )
    },
    {
      field: 'bundle',
      hideable: false,
      headerName: '묶음*',
      renderCell: params => (
        <Checkbox
          checked={params.row.bundle == '1'}
          onChange={(e) => handleCheckboxChange(params.row.cartid, 'bundle', e.target.checked)}
        />
      )
    },
    {
      field: 'ps',
      hideable: false,
      headerName: 'PS*',
      renderCell: params => (
        <Checkbox
          checked={params.row.ps == '1'}
          onChange={(e) => handleCheckboxChange(params.row.cartid, 'ps', e.target.checked)}
        />
      )
    },
    {
      field: 'pkg',
      hideable: false,
      headerName: 'PKG*',
      renderCell: params => (
        <Checkbox
          checked={params.row.pkg == '1'}
          onChange={(e) => handleCheckboxChange(params.row.cartid, 'pkg', e.target.checked)}
        />
      )
    },
    {
      field: 'import_country',
      hideable: false,
      headerName: '수입국',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.import_country}
        </Typography>
      )
    },
    {
      field: 'stock_kr',
      hideable: false,
      headerName: '국내재고',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.stock_kr}
        </Typography>
      )
    },
    {
      field: 'stock_intl',
      hideable: false,
      headerName: '해외재고',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.stock_intl}
        </Typography>
      )
    },
    {
      field: 'end_user',
      hideable: false,
      headerName: '엔드유저*',
      editable: true,
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.end_user}
        </Typography>
      )
    },
    {
      field: 'remarks',
      hideable: false,
      headerName: '메모*',
      editable: true,
      renderCell: (params) => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.remarks}
        </Typography>
      )
    },
    {
      field: 'order_status',
      minWidth: 150,
      hideable: false,
      headerName: '진행상황',      
      renderCell: params => {  
        return (
          <Select
            value={params.row.order_status}
            onChange={(e) => {
              handleStateChange(params.row.cartid, 'order_status', e.target.value);
            }}
            size="small"
            native
          >
            <option>신규견적</option>
            <option>견적완료</option>
            <option>미발주</option>
            <option>신규주문</option>
            <option>주문완료</option>
            <option>신고관리</option>
            <option>배송완료</option>
            <option>주문취소</option>
            <option>취소완료</option>
          </Select>
        )
      }
    }
  ]

  return (
    loading ?
    <Spinner/> :
    <StyledDataGrid>
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.cartid}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: OrderToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            numSelected: selected.length || 0,
            selected,
            setList,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.cartid),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
        onCellEditStop={(params, event) => {
          if (params.reason === GridCellEditStopReasons.enterKeyDown) {
            // event.defaultMuiPrevented = true;            
            if (event.target.value?.length === 0) {
              return
            }

            setList(list.map(item => {
              if (item.cartid == params.id) {
                  return { ...item, 
                    [params.field]: event.target.value
                  };
              }
              return item;
            }));

            // 이미 선택된 row가 수정된 경우
            setSelected(
              list.filter(item => {
                if (item.cartid == params.id) {
                  return { ...item, 
                    [params.field]: event.target.value
                  };
              }
              return item;
            }));

          }
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
      />
    </StyledDataGrid>
  )
}

export default TableOrderManage